import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * カメラ映像から QR を読み続けるフック。
 *
 * ## 二段構え
 *
 * 1. **`BarcodeDetector`**(ブラウザ内蔵)。あれば最優先。ネイティブ実装なので速く、
 *    CPU も食わない。Chrome/Edge 系で使える
 * 2. **jsQR**(JS実装)にフォールバック。iOS Safari など `BarcodeDetector` を
 *    持たない環境で使う。canvas に描いて1フレームずつ解く
 *
 * 想定端末は**カメラ付きノートPC / タブレット / スマートフォンの3種**(代表決定 2026-08-27)。
 * 特定機種に依存させないため、どちらの経路でも動くことを受け入れ条件にしている。
 *
 * ## jsQR は必要になってから読む
 *
 * jsQR は画像処理を含むぶん重い。**共用端末を使わない利用者(大多数)にまで
 * 配ると全員の初回表示が遅くなる**ので、静的 import ではなく動的 import にして
 * `BarcodeDetector` が無い環境でだけ落としてくる。
 *
 * ⚠️ **失敗の切り分けを潰さないこと。** 以前はカメラ取得と jsQR の読み込みを
 * 同じ try に入れていたため、**QRライブラリの読み込みに失敗しても
 * 「カメラを使用できません」と表示していた**(古い iOS で実際に誤診した)。
 * どこで失敗したかを `failure` で返す。
 *
 * ## カメラは選べるようにする
 *
 * 既定は背面(`environment`)だが、**内カメラしか無い端末や、
 * 壁掛けで内カメラを使いたい設置**がある。切り替えられないと詰むので、
 * 実際に繋がっているカメラを列挙して選べるようにし、選択は端末に覚えさせる
 * (設置したら基本変えないため、サーバではなく localStorage が適切)。
 *
 * ## カメラは必ず止める
 *
 * 店頭に置きっぱなしの端末なので、画面を離れてもカメラが回り続けると
 * 発熱と電池消費が問題になる。アンマウント時に確実に track を stop する。
 */

type Detector = { detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>> };

export type ScannerStatus = 'idle' | 'starting' | 'scanning' | 'failed';

/** 何が原因で動かないのか。案内文を出し分けるために区別する */
export type ScannerFailure =
  | { kind: 'no_api' }                     // ブラウザがカメラに対応していない
  | { kind: 'denied' }                     // 利用者が許可しなかった
  | { kind: 'no_camera' }                  // カメラが見つからない
  | { kind: 'camera_error'; detail: string } // その他のカメラ側の失敗
  | { kind: 'decoder_failed' };            // QR読み取りの読み込みに失敗

const CAMERA_PREF_KEY = 'kinlog_kiosk_camera';

function savedCameraId(): string | null {
  try {
    return localStorage.getItem(CAMERA_PREF_KEY);
  } catch {
    return null; // プライベートモード等で読めなくても動かす
  }
}

function rememberCamera(id: string) {
  try {
    localStorage.setItem(CAMERA_PREF_KEY, id);
  } catch {
    /* 覚えられなくても今回のセッションでは使える */
  }
}

interface Options {
  /** QR を読めたときに呼ばれる。同じ値の連続通知は抑制済み */
  onDetect: (value: string) => void;
  /** false の間は読み取りを止める(打刻完了の表示中など) */
  active: boolean;
}

export function useQrScanner({ onDetect, active }: Options) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const detectorRef = useRef<Detector | null>(null);
  /** jsQR は必要になった時点で読み込む(バンドルを全利用者に配らないため) */
  const jsQrRef = useRef<((d: Uint8ClampedArray, w: number, h: number, o?: any) => any) | null>(null);

  // 直近に通知した値。同じQRをかざし続けても1回しか呼ばない
  const lastValueRef = useRef<string | null>(null);
  const onDetectRef = useRef(onDetect);
  onDetectRef.current = onDetect;

  const [status, setStatus] = useState<ScannerStatus>('idle');
  const [failure, setFailure] = useState<ScannerFailure | null>(null);
  /** ネイティブ実装が使えたか。実機確認のために画面へ出す */
  const [engine, setEngine] = useState<'native' | 'jsqr' | null>(null);

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState<string | null>(savedCameraId());

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  /** 直近値を忘れる。同じ人が続けて2回打つ(休憩開始→終了)ときに要る */
  const resetLastValue = useCallback(() => {
    lastValueRef.current = null;
  }, []);

  /** 次のカメラへ切り替える。選択は端末に覚えさせる */
  const switchCamera = useCallback(() => {
    setCameraId((current) => {
      if (cameras.length === 0) return current;
      const i = cameras.findIndex((c) => c.deviceId === current);
      const next = cameras[(i + 1) % cameras.length];
      rememberCamera(next.deviceId);
      return next.deviceId;
    });
  }, [cameras]);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setFailure({ kind: 'no_api' });
        setStatus('failed');
        return;
      }
      setStatus('starting');
      setFailure(null);

      // --- 1) カメラを取る ---
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // 覚えている端末があればそれを。無ければ背面優先。
          // `ideal` にしているのは、背面が無い端末で失敗させないため
          video: cameraId
            ? { deviceId: { exact: cameraId } }
            : { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch (e: any) {
        if (cancelled) return;
        const name = e?.name ?? '';
        // 覚えていたカメラが無くなっている場合は、指定を捨ててやり直す
        if (cameraId && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
          try { localStorage.removeItem(CAMERA_PREF_KEY); } catch { /* noop */ }
          setCameraId(null);
          return;
        }
        setFailure(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? { kind: 'denied' }
            : name === 'NotFoundError' || name === 'DevicesNotFoundError'
              ? { kind: 'no_camera' }
              : { kind: 'camera_error', detail: name || String(e?.message ?? e) }
        );
        setStatus('failed');
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      // iOS Safari は playsInline が無いと全画面再生に飛ぶ
      video.setAttribute('playsinline', 'true');
      try {
        await video.play();
      } catch {
        /* 自動再生が拒否されても、多くの環境では描画は進む */
      }

      // 許可が下りて初めてラベル付きで列挙できる
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!cancelled) {
          const videos = devices.filter((d) => d.kind === 'videoinput');
          setCameras(videos);
          // いま使っているカメラを控えておく（切り替えの起点にする）
          const track = stream.getVideoTracks()[0];
          const id = track?.getSettings?.().deviceId;
          if (id && !cameraId) setCameraId(id);
        }
      } catch {
        /* 列挙できなくても読み取り自体は動く */
      }

      // --- 2) 読み取り手段を用意する（カメラとは別の失敗として扱う）---
      const Ctor = (window as any).BarcodeDetector;
      if (Ctor) {
        try {
          detectorRef.current = new Ctor({ formats: ['qr_code'] });
          setEngine('native');
        } catch {
          detectorRef.current = null;
        }
      }

      if (!detectorRef.current) {
        try {
          const mod = await import('jsqr');
          if (cancelled) return;
          jsQrRef.current = mod.default;
          setEngine('jsqr');
        } catch {
          if (cancelled) return;
          // ⚠️ ここを「カメラの失敗」と混ぜない。原因がまるで違う
          setFailure({ kind: 'decoder_failed' });
          setStatus('failed');
          return;
        }
      }

      if (!cancelled) {
        setStatus('scanning');
        rafRef.current = requestAnimationFrame(tick);
      }
    }

    async function tick() {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      let value: string | null = null;

      try {
        if (detectorRef.current) {
          const found = await detectorRef.current.detect(video);
          value = found[0]?.rawValue ?? null;
        } else if (jsQrRef.current) {
          // jsQR は画素配列を要求するので canvas を経由する。
          // 解像度は 480px 相当まで落とす — フル解像度だと1フレームの解析が重く、
          // 端末によっては読み取りの体感が悪化する
          const scale = Math.min(1, 480 / (video.videoWidth || 480));
          canvas.width = Math.floor((video.videoWidth || 480) * scale);
          canvas.height = Math.floor((video.videoHeight || 360) * scale);
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const result = jsQrRef.current(image.data, image.width, image.height, {
              inversionAttempts: 'dontInvert',
            });
            value = result?.data ?? null;
          }
        }
      } catch {
        // 1フレームの失敗は無視して次を読む。ここで止めるとカメラが死んだように見える
      }

      if (value && value !== lastValueRef.current) {
        lastValueRef.current = value;
        onDetectRef.current(value);
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    if (active) {
      start();
    } else {
      stop();
      setStatus('idle');
    }

    return () => {
      cancelled = true;
      stop();
    };
  }, [active, stop, cameraId]);

  return { videoRef, canvasRef, status, failure, engine, resetLastValue, cameras, cameraId, switchCamera };
}
